import { useEffect, useMemo, useRef, useState } from 'react'
import type { TmdbEpisode, TmdbSeason } from '../tmdb/tmdb'
import type { CloudEpisodeState } from '@govu/shared'
import { LazyImage } from '../ui'

export interface EpisodeCloudLookup {
  (seasonNumber: number, episodeNumber: number): CloudEpisodeState | undefined
}

interface Props {
  season: TmdbSeason | null
  episodes: TmdbEpisode[] | null
  loading: boolean
  /** Cloud state accessor. Returns `undefined` if no state is recorded. */
  lookupCloud: EpisodeCloudLookup
  /** TMDB id for the series, used by the title row for accessibility. */
  seriesTmdbId: number
  /** Bold marker on the single episode the user was last watching. */
  lastWatchedEpisodeKey: string | null
  onPlay: (episode: TmdbEpisode) => void
  onToggleWatched: (episode: TmdbEpisode, nextWatched: boolean) => void
  onMarkWatchedUntil: (episode: TmdbEpisode) => void
  /** Optional "previous season" / "next season" inline buttons (desktop parity). */
  hasPrevSeason?: boolean
  hasNextSeason?: boolean
  onPrevSeason?: () => void
  onNextSeason?: () => void
  writeBusy?: boolean
}

interface ContextMenuState {
  visible: boolean
  x: number
  y: number
  episode: TmdbEpisode | null
}

/**
 * Two-pane episode browser that mirrors the desktop `EpisodeList` layout
 * at 1:1 fidelity:
 *
 *   - Left: a vertical list of every episode in the season, with the
 *     episode number, title, watched indicator, and a subtle dot next
 *     to the user's last-watched episode. Scrollable on overflow.
 *
 *   - Right: a focused-episode card with a 16:9 still, runtime, air
 *     date, TMDB rating, overview, "Mark watched / Until here / Play"
 *     action row, and an in-progress bar drawn over the still.
 *
 *   - Season progress bar above the list showing watched / total.
 *
 *   - On narrow viewports the two panes stack so the layout stays
 *     usable on phones. On wider screens they sit side-by-side.
 *
 * Right-clicking an episode opens a context menu (Play / Mark watched /
 * Until here) to match the desktop's power-user affordance.
 */
export function SeriesEpisodeList({
  season,
  episodes,
  loading,
  lookupCloud,
  lastWatchedEpisodeKey,
  onPlay,
  onToggleWatched,
  onMarkWatchedUntil,
  hasPrevSeason,
  hasNextSeason,
  onPrevSeason,
  onNextSeason,
  writeBusy,
}: Props) {
  const defaultFocusKey = useMemo(() => {
    if (!episodes || episodes.length === 0) return null
    // Prefer the last-watched episode if it's in this season.
    if (lastWatchedEpisodeKey) {
      const match = episodes.find(
        (e) => episodeKey(e.seasonNumber, e.episodeNumber) === lastWatchedEpisodeKey
      )
      if (match) return episodeKey(match.seasonNumber, match.episodeNumber)
    }
    // Otherwise focus the first un-watched episode.
    const firstUnwatched = episodes.find(
      (e) => !lookupCloud(e.seasonNumber, e.episodeNumber)?.isWatched
    )
    if (firstUnwatched) {
      return episodeKey(firstUnwatched.seasonNumber, firstUnwatched.episodeNumber)
    }
    // Fall back to the first episode.
    return episodeKey(episodes[0].seasonNumber, episodes[0].episodeNumber)
  }, [episodes, lookupCloud, lastWatchedEpisodeKey])

  const [focusedKey, setFocusedKey] = useState<string | null>(defaultFocusKey)
  const focusedRowRef = useRef<HTMLButtonElement | null>(null)

  useEffect(() => {
    setFocusedKey(defaultFocusKey)
  }, [defaultFocusKey])

  useEffect(() => {
    focusedRowRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
  }, [focusedKey])

  const [contextMenu, setContextMenu] = useState<ContextMenuState>({
    visible: false,
    x: 0,
    y: 0,
    episode: null,
  })

  useEffect(() => {
    if (!contextMenu.visible) return
    const close = () => setContextMenu((m) => ({ ...m, visible: false }))
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close()
    }
    document.addEventListener('click', close)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('click', close)
      document.removeEventListener('keydown', onKey)
    }
  }, [contextMenu.visible])

  if (loading && (!episodes || episodes.length === 0)) {
    return (
      <div className="space-y-3">
        <div className="h-2 w-full rounded-full bg-bg-secondary" />
        <div className="flex flex-col gap-3 md:flex-row">
          <div className="flex w-full flex-col gap-1 md:w-72">
            {[0, 1, 2, 3, 4].map((i) => (
              <div
                key={i}
                className="h-9 w-full animate-pulse rounded-md bg-bg-secondary"
              />
            ))}
          </div>
          <div className="flex-1 rounded-xl border border-border-primary bg-bg-secondary/40 p-4">
            <div className="aspect-video w-full animate-pulse rounded-md bg-bg-tertiary" />
          </div>
        </div>
      </div>
    )
  }

  if (!episodes || episodes.length === 0) {
    return (
      <div className="rounded-xl border border-dashed border-border-primary bg-bg-secondary/40 py-8 text-center text-xs text-text-tertiary">
        No episodes yet for this season.
      </div>
    )
  }

  const focused =
    episodes.find(
      (e) => episodeKey(e.seasonNumber, e.episodeNumber) === focusedKey
    ) ?? episodes[0]

  const focusedCloud = lookupCloud(focused.seasonNumber, focused.episodeNumber)
  const focusedWatched = !!focusedCloud?.isWatched
  const focusedProgressPct =
    focusedCloud?.progress && focused.runtime
      ? Math.min(100, Math.round((focusedCloud.progress / (focused.runtime * 60)) * 100))
      : 0

  const watchedCount = episodes.filter(
    (e) => !!lookupCloud(e.seasonNumber, e.episodeNumber)?.isWatched
  ).length

  const handleContextMenu = (e: React.MouseEvent, episode: TmdbEpisode) => {
    e.preventDefault()
    e.stopPropagation()
    setContextMenu({ visible: true, x: e.clientX, y: e.clientY, episode })
  }

  return (
    <div>
      {/* Season header row */}
      {season && (
        <div className="mb-3 flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-text-tertiary">
          <span className="text-text-secondary">{season.name}</span>
          <span>·</span>
          <span>
            {episodes.length} episode{episodes.length === 1 ? '' : 's'}
          </span>
          {season.airDate && (
            <>
              <span>·</span>
              <span>{new Date(season.airDate).getFullYear()}</span>
            </>
          )}
        </div>
      )}

      {/* Season progress bar */}
      <div className="mb-4 flex items-center gap-3">
        <div className="h-1 flex-1 overflow-hidden rounded-full bg-bg-secondary">
          <div
            className="h-full rounded-full bg-accent-primary transition-all duration-500"
            style={{ width: `${(watchedCount / episodes.length) * 100}%` }}
          />
        </div>
        <span className="flex-shrink-0 text-[11px] tabular-nums text-text-tertiary">
          {watchedCount}/{episodes.length}
        </span>
      </div>

      <div className="flex flex-col gap-5 md:flex-row">
        {/* Left pane: episode list */}
        <div className="flex w-full flex-col gap-0.5 overflow-y-auto pr-1 md:max-h-[28rem] md:w-72 md:flex-shrink-0">
          {hasPrevSeason && (
            <button
              type="button"
              onClick={onPrevSeason}
              className="flex items-center justify-center gap-1.5 rounded-md px-3 py-1.5 text-[11px] text-text-tertiary transition-colors hover:bg-bg-secondary hover:text-text-primary"
            >
              <svg
                className="h-3 w-3"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M5 15l7-7 7 7"
                />
              </svg>
              Previous season
            </button>
          )}

          {episodes.map((ep) => {
            const key = episodeKey(ep.seasonNumber, ep.episodeNumber)
            const isFocused = key === episodeKey(focused.seasonNumber, focused.episodeNumber)
            const cloud = lookupCloud(ep.seasonNumber, ep.episodeNumber)
            const watched = !!cloud?.isWatched
            const isLastWatched = key === lastWatchedEpisodeKey
            return (
              <button
                key={key}
                type="button"
                ref={isFocused ? focusedRowRef : undefined}
                onClick={() => setFocusedKey(key)}
                onContextMenu={(e) => handleContextMenu(e, ep)}
                className={`flex w-full items-center gap-2.5 rounded-md px-3 py-2 text-left transition-colors duration-150 ${
                  isFocused
                    ? 'bg-bg-tertiary text-text-primary'
                    : 'text-text-secondary hover:bg-bg-secondary hover:text-text-primary'
                }`}
              >
                <span
                  className={`w-5 flex-shrink-0 text-center font-mono text-[11px] ${
                    watched ? 'text-accent-primary' : 'text-text-tertiary'
                  }`}
                >
                  {ep.episodeNumber}
                </span>
                <span className="flex-1 truncate text-[13px]">{ep.name}</span>
                {isLastWatched && (
                  <span
                    aria-hidden
                    className="h-2 w-2 flex-shrink-0 animate-pulse rounded-full bg-accent-primary"
                  />
                )}
              </button>
            )
          })}

          {hasNextSeason && (
            <button
              type="button"
              onClick={onNextSeason}
              className="flex items-center justify-center gap-1.5 rounded-md px-3 py-1.5 text-[11px] text-text-tertiary transition-colors hover:bg-bg-secondary hover:text-text-primary"
            >
              Next season
              <svg
                className="h-3 w-3"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M19 9l-7 7-7-7"
                />
              </svg>
            </button>
          )}
        </div>

        {/* Right pane: focused episode card */}
        <div className="min-w-0 flex-1">
          <div className="flex flex-col gap-4 rounded-xl border border-border-primary bg-bg-secondary/40 p-4 sm:flex-row">
            {/* Still */}
            <div className="relative aspect-video w-full flex-shrink-0 self-start overflow-hidden rounded-lg bg-bg-tertiary sm:w-64">
              {focused.stillUrl ? (
                <LazyImage
                  src={focused.stillUrl}
                  alt={focused.name}
                  className="h-full w-full"
                  placeholderColor="#1a1a1a"
                  fallback={
                    <div className="flex h-full w-full items-center justify-center text-text-tertiary">
                      <svg
                        className="h-10 w-10 opacity-40"
                        fill="currentColor"
                        viewBox="0 0 24 24"
                        aria-hidden
                      >
                        <path d="M8 5v14l11-7z" />
                      </svg>
                    </div>
                  }
                />
              ) : (
                <div className="flex h-full w-full items-center justify-center text-text-tertiary">
                  <svg
                    className="h-10 w-10 opacity-40"
                    fill="currentColor"
                    viewBox="0 0 24 24"
                    aria-hidden
                  >
                    <path d="M8 5v14l11-7z" />
                  </svg>
                </div>
              )}
              <button
                type="button"
                onClick={() => onPlay(focused)}
                aria-label={`Play ${focused.name}`}
                className="absolute inset-0 flex items-center justify-center bg-black/20 opacity-0 transition-opacity hover:opacity-100"
              >
                <div className="flex h-12 w-12 items-center justify-center rounded-full bg-accent-primary/90 text-black shadow-cinematic transition-transform hover:scale-110">
                  <svg
                    className="ml-0.5 h-5 w-5"
                    fill="currentColor"
                    viewBox="0 0 24 24"
                    aria-hidden
                  >
                    <path d="M8 5v14l11-7z" />
                  </svg>
                </div>
              </button>
              {!focusedWatched && focusedProgressPct > 0 && (
                <div className="absolute bottom-0 left-0 right-0 h-1 bg-black/40">
                  <div
                    className="h-full bg-accent-primary"
                    style={{ width: `${focusedProgressPct}%` }}
                  />
                </div>
              )}
            </div>

            {/* Info */}
            <div className="flex min-w-0 flex-1 flex-col gap-2">
              <div className="flex flex-wrap items-center gap-2">
                <span className="font-mono text-[11px] text-text-tertiary">
                  S{focused.seasonNumber}E{focused.episodeNumber}
                </span>
                {episodeKey(focused.seasonNumber, focused.episodeNumber) ===
                  lastWatchedEpisodeKey && (
                  <span className="rounded bg-accent-primary px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider text-black">
                    Resume
                  </span>
                )}
                {focusedWatched && (
                  <span className="rounded bg-accent-primary/20 px-1.5 py-0.5 text-[9px] font-medium uppercase tracking-wider text-accent-primary">
                    Watched
                  </span>
                )}
              </div>

              <h3 className="text-[15px] font-semibold leading-snug text-text-primary">
                {focused.name}
              </h3>

              <div className="flex flex-wrap items-center gap-x-2.5 gap-y-0.5 text-[11px] text-text-tertiary">
                {focused.runtime != null && <span>{focused.runtime} min</span>}
                {focused.airDate && (
                  <>
                    {focused.runtime != null && <span className="opacity-40">|</span>}
                    <span>{new Date(focused.airDate).toLocaleDateString()}</span>
                  </>
                )}
                {focused.rating > 0 && (
                  <>
                    <span className="opacity-40">|</span>
                    <span className="flex items-center gap-1 text-yellow-400">
                      ★ {focused.rating.toFixed(1)}
                    </span>
                  </>
                )}
              </div>

              {focused.overview && (
                <p className="line-clamp-4 text-[13px] leading-relaxed text-text-secondary">
                  {focused.overview}
                </p>
              )}

              <div className="mt-auto flex flex-wrap items-center gap-2 pt-2">
                <button
                  type="button"
                  disabled={writeBusy}
                  onClick={() => onToggleWatched(focused, !focusedWatched)}
                  className={`flex h-8 items-center gap-1.5 rounded-md px-3 text-[11px] font-medium transition-all disabled:opacity-50 ${
                    focusedWatched
                      ? 'bg-accent-primary/20 text-accent-primary hover:bg-accent-primary/30'
                      : 'bg-bg-tertiary text-text-secondary hover:bg-bg-primary hover:text-text-primary'
                  }`}
                  title={
                    focusedWatched ? 'Mark as not watched' : 'Mark as watched'
                  }
                >
                  <svg
                    className="h-3.5 w-3.5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M5 13l4 4L19 7"
                    />
                  </svg>
                  {focusedWatched ? 'Watched' : 'Mark watched'}
                </button>

                <button
                  type="button"
                  disabled={writeBusy}
                  onClick={() => onMarkWatchedUntil(focused)}
                  className="flex h-8 items-center gap-1.5 rounded-md bg-bg-tertiary px-3 text-[11px] font-medium text-text-secondary transition-all hover:bg-bg-primary hover:text-text-primary disabled:opacity-50"
                  title="Mark everything up to here as watched"
                >
                  <svg
                    className="h-3.5 w-3.5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M4 6h16M4 12h10"
                    />
                  </svg>
                  Until here
                </button>

                <button
                  type="button"
                  onClick={() => onPlay(focused)}
                  className="flex h-8 items-center gap-1.5 rounded-md bg-accent-primary px-3 text-[11px] font-semibold text-black transition-all hover:bg-accent-hover"
                  title="Play"
                >
                  <svg
                    className="ml-0.5 h-3.5 w-3.5"
                    fill="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path d="M8 5v14l11-7z" />
                  </svg>
                  Play
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Context menu */}
      {contextMenu.visible && contextMenu.episode && (
        <div
          role="menu"
          className="fixed z-50 min-w-[200px] rounded-lg border border-border-primary bg-bg-secondary py-1.5 shadow-cinematic-lg backdrop-blur-xl"
          style={{ left: contextMenu.x, top: contextMenu.y }}
        >
          <div className="px-3 py-1.5 text-[10px] font-medium uppercase tracking-wider text-text-tertiary">
            S{contextMenu.episode.seasonNumber}E{contextMenu.episode.episodeNumber}
          </div>

          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation()
              if (contextMenu.episode) {
                const watched = !!lookupCloud(
                  contextMenu.episode.seasonNumber,
                  contextMenu.episode.episodeNumber
                )?.isWatched
                onToggleWatched(contextMenu.episode, !watched)
              }
              setContextMenu((m) => ({ ...m, visible: false }))
            }}
            className="flex w-full items-center gap-2.5 px-3 py-2 text-sm text-text-secondary transition-colors hover:bg-bg-tertiary hover:text-text-primary"
          >
            <svg
              className="h-4 w-4 text-text-tertiary"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M5 13l4 4L19 7"
              />
            </svg>
            {!!lookupCloud(
              contextMenu.episode.seasonNumber,
              contextMenu.episode.episodeNumber
            )?.isWatched
              ? 'Mark as not watched'
              : 'Mark as watched'}
          </button>

          <div className="mx-2 my-1 h-px bg-border-primary" />

          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation()
              if (contextMenu.episode) onMarkWatchedUntil(contextMenu.episode)
              setContextMenu((m) => ({ ...m, visible: false }))
            }}
            className="flex w-full items-center gap-2.5 px-3 py-2 text-sm text-text-secondary transition-colors hover:bg-bg-tertiary hover:text-text-primary"
          >
            <svg
              className="h-4 w-4 text-text-tertiary"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 5l7 7-7 7"
              />
            </svg>
            Watched until here
          </button>

          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation()
              if (contextMenu.episode) onPlay(contextMenu.episode)
              setContextMenu((m) => ({ ...m, visible: false }))
            }}
            className="flex w-full items-center gap-2.5 px-3 py-2 text-sm text-text-secondary transition-colors hover:bg-bg-tertiary hover:text-text-primary"
          >
            <svg
              className="h-4 w-4 text-text-tertiary"
              fill="currentColor"
              viewBox="0 0 24 24"
            >
              <path d="M8 5v14l11-7z" />
            </svg>
            Play episode
          </button>
        </div>
      )}
    </div>
  )
}

export function episodeKey(seasonNumber: number, episodeNumber: number): string {
  return `${seasonNumber}:${episodeNumber}`
}
